import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import CalendarUtils from 'services/CalendarUtils';
import PropTypes from 'prop-types';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import SelectableListItem from 'components/Utils/SelectableListItem';
import './style.scss';
import { AddCardModal } from 'components/Payments/CreditCardScreen/view';
import ChargePatientForm from 'components/Payments/ChargePatientForm';
import DefaultModal from 'components/Utils/DefaultModal';
import Icon from 'components/Utils/NewIcon';
import DefaultChip from 'components/Utils/DefaultChip';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppSelector } from 'careand-redux/store';
import { UncontrolledCollapse } from 'components/Utils/Collapse';
import SubscriptionUtils from 'services/SubscriptionUtils';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import ConfirmationButton from '../../Utils/NewDefaultButton/ConfirmationButton';

const CreditCardList = (props) => (
  <div className="credit-card-list">
    <span className="small-title">
      Credit Cards
      <Icon
        src="circle-info"
        showTooltip={true}
        tooltipPlace="right"
        alt="Click on a card to remove it"
        style={{
          display: 'inline-block',
          top: '5px',
          left: '10px',
          position: 'relative',
        }}
        width="20px"
      />
    </span>
    <List>
      <ListItems {...props} />
    </List>
    <div className="buttons">
      <PromoCodeButtons {...props} />
      <AddCreditCard {...props} />
      <ChargePatientButton {...props} />
      <SubscribeButton {...props} />
    </div>
  </div>
);

const PromoCodeButtons = ({ patient }) => {
  const dispatch = useAppDispatch();
  const isLoadingDiscountPromoCode = useIsLoading(api.Payments.applyDiscountPromoCode);
  const isLoadingSend = useIsLoading(api.Payments.sendPromoCodeEmail);
  const isLoadingAdd = useIsLoading(api.Payments.applyPromoCode);

  const subscription = useAppSelector((state) => state.payments.subscriptions[patient.id]);
  const unsubscribed = SubscriptionUtils.isUnsubscribed(subscription);

  async function applyDiscountPromoCode() {
    await dispatch(api.Payments.applyDiscountPromoCode({ patientId: patient.id }), {
      title: 'Promo Code',
      successText: "Promo Code has been added to the patient's account",
    });
  }

  async function onSendPromoCode() {
    await dispatch(api.Payments.sendPromoCodeEmail({ patientId: patient.id }), {
      title: 'Promo Code',
      successText: 'Promo Code email scheduled to be sent in the next 5 minutes',
    });
  }

  async function applyPromoCode(promoCode) {
    if (promoCode) {
      await dispatch(api.Payments.applyPromoCode({ patientId: patient.id, promoCode }), {
        title: 'Promo Code',
        successText: "Promo Code added to the patient's account",
      });
      await dispatch(api.Patients.getPatient({ id: patient.id }));
    }
  }

  return (
    <>
      <div className="button-wrapper add-credit-card">
        <ConfirmationButton
          name="Apply Specific Promo Code"
          icon="percent"
          onClick={(e, form) => applyPromoCode(form['Promo Code'])}
          loading={isLoadingAdd}
          confirmation={{
            message: "This will apply the promo code directly to the patient's account.",
            form: [{ type: 'textField', label: 'Promo Code' }],
          }}
        />
      </div>
      {unsubscribed && (
        <div className="row">
          <div className="button-wrapper add-credit-card">
            <ConfirmationButton
              name="Apply PPU Promo"
              icon="percent"
              onClick={applyDiscountPromoCode}
              loading={isLoadingDiscountPromoCode}
              confirmation={{
                message:
                  "This will apply the subscription promo code directly to the patient's account.",
              }}
            />
          </div>
          <div className="button-wrapper add-credit-card">
            <ConfirmationButton
              name="Send PPU Promo"
              icon="envelope"
              onClick={onSendPromoCode}
              loading={isLoadingSend}
              confirmation={{
                message: "This will send a subscription promo code to the patient's email.",
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

const AddCreditCard = (props) => {
  const [showModal, setShowModal] = useState(false);
  return (
    <div className="button-wrapper add-credit-card">
      <AddCardModal
        showModal={showModal}
        onAddSuccess={() => setShowModal(false)}
        patient={props.patient}
        openModal={setShowModal}
      />
      <DefaultButton name="Add Credit Card" icon="credit-card" onClick={() => setShowModal(true)} />
    </div>
  );
};

const SubscribeButton = ({ patient, creditCards }) => {
  const dispatch = useAppDispatch();
  const isUnsubscribing = useIsLoading(api.Payments.unsubscribe);
  const loadingRetryPayment = useIsLoading(api.Payments.retryPayment);
  const [showModal, setShowModal] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);

  const subscription = useAppSelector((state) => state.payments.subscriptions[patient.id]);
  const unsubscribed = SubscriptionUtils.isUnsubscribed(subscription);
  const isOverdue = SubscriptionUtils.isPaymentOverdue(subscription);

  // Mirrors `assertChangeablePlan` in the backend
  // (src/api/controllers/common/subscriptions.ts). Everything it rejects
  // outright is hidden here rather than shown and then 400'd:
  //  - no/`corp_` subscription id: corporate subscriptions are synthetic;
  //  - status other than `active` (notably `past_due`, which is where the
  //    sibling Retry Payment button lives) — note SubscriptionUtils
  //    .isActiveSubscription also accepts `trialing`, which the backend does
  //    not, so the status is compared directly;
  //  - already scheduled for cancellation.
  const canChangePlan =
    !unsubscribed &&
    !!subscription?.id &&
    subscription.id.startsWith('sub_') &&
    subscription.status === 'active' &&
    !SubscriptionUtils.isScheduledToCancel(subscription);

  async function onCancel() {
    await dispatch(api.Payments.unsubscribe({ userId: patient.id }), {
      title: 'Cancel Subscription',
      successText: 'Subscription cancelled successfully',
    });
  }

  async function onRetryPayment() {
    await dispatch(api.Payments.retryPayment({ patientId: patient.id }), {
      title: 'Retry Payment',
      successText: 'Payment successful!',
    });
  }

  return (
    <>
      <div className="button-wrapper subscribe-patient">
        <ConfirmationButton
          name="Cancel Subscription"
          hide={unsubscribed}
          onClick={onCancel}
          loading={isUnsubscribing}
          icon="xmark"
          confirmation={{
            message:
              "Are you sure you want to cancel this patient's subscription? It will cancel at the end of the current billing period.",
          }}
        />
      </div>
      <div className="button-wrapper subscribe-patient">
        {!unsubscribed && isOverdue && (
          <DefaultButton
            name="Retry Payment"
            onClick={onRetryPayment}
            loading={loadingRetryPayment}
            icon="arrow-rotate-right"
          />
        )}
        {unsubscribed && (
          <DefaultButton
            name="Add Subscription"
            disabled={!creditCards?.length}
            onClick={() => setShowModal(true)}
            icon="arrow-rotate-right"
          />
        )}
        {canChangePlan && (
          <DefaultButton
            name="Change Plan"
            disabled={!creditCards?.length}
            onClick={() => setShowChangePlanModal(true)}
            icon="arrow-up-right-dots"
          />
        )}
        {showChangePlanModal && (
          <DefaultModal
            className="modal subscribe-patient-modal change-plan-modal fit-modal"
            wrap={true}
            padding={true}
            title="Change Plan"
            content={
              <ChangePlan
                patient={patient}
                subscription={subscription}
                onSuccess={() => setShowChangePlanModal(false)}
              />
            }
            isOpen={showChangePlanModal}
            onRequestClose={() => setShowChangePlanModal(false)}
          />
        )}
        {showModal && (
          <DefaultModal
            className="modal subscribe-patient-modal fit-modal"
            wrap={true}
            padding={true}
            title="Add Subscription"
            content={<AddSubscription patient={patient} onSuccess={() => setShowModal(false)} />}
            isOpen={showModal}
            onRequestClose={() => setShowModal(false)}
          />
        )}
      </div>
    </>
  );
};

const AddSubscription = ({ patient, onSuccess }) => {
  const dispatch = useAppDispatch();
  const plans = useAppSelector((state) => state.payments.clinic_payment_plans);
  const [plan, setPlan] = useState(null);
  const isLoading = useIsLoading(api.Payments.createSubscription);

  useEffect(() => {
    if (plans.length === 0) dispatch(api.Payments.getAllPaymentPlans());
  }, [dispatch, plans]);

  const filteredPlans = useMemo(() => {
    return plans.filter(
      (plan) => plan.payment_plan?.amount && plan.payment_plan?.interval === 'year',
    );
  }, [plans]);

  async function onSubscribe() {
    let result = await dispatch(
      api.Payments.createSubscription({
        paymentPlanId: plan.id,
        patientId: patient.id,
      }),
      {
        title: 'Subscription',
        successText: 'Subscription created successfully!',
      },
    );

    if (result) {
      onSuccess();
    }
  }

  return (
    <div className="add-subscription">
      <PlanChips
        title="Active Plans"
        plans={filteredPlans.filter(({ payment_plan }) => payment_plan.active)}
        onClick={setPlan}
        activeId={plan?.id}
      />
      <PlanChips
        title="Archived Plans"
        plans={filteredPlans.filter(({ payment_plan }) => !payment_plan.active)}
        onClick={setPlan}
        activeId={plan?.id}
        collapsed={true}
      />

      <div className="button-wrapper">
        <ConfirmationButton
          name={plan ? 'Subscribe to ' + getPlanDescription(plan) : 'Add Subscription'}
          loading={isLoading}
          onClick={onSubscribe}
          disabled={!plan}
          className="blue"
          style={{ width: '100%' }}
          confirmation={{
            message: `This will charge the patient $${SubscriptionUtils.convertPlanAmount(
              plan?.payment_plan?.amount,
            )} and start the ${
              plan?.payment_plan.name
            } subscription. Are you sure you want to proceed?`,
          }}
        />
      </div>
    </div>
  );
};

/**
 * The backend refuses a `proration_date` older than 15 minutes. Treat a quote as
 * expired a little before that, so staff are told to refresh rather than being
 * bounced by a 400 they cannot see coming.
 *
 * This deliberately shadows `PRORATION_DATE_MAX_AGE_SECONDS` in the backend's
 * `src/api/controllers/common/subscriptions.ts` (see `assertFreshProrationDate`).
 * The two are not shared through the API, so if either is changed the other has
 * to be changed with it — this value must stay strictly below the backend's.
 */
const QUOTE_STALE_AFTER_SECONDS = 13 * 60;

/**
 * The backend's quote-expiry sentence, from `assertFreshProrationDate`:
 * "Error: This quote has expired. Preview the plan change again before confirming."
 *
 * The match MUST stay this narrow. A bare /expired/i also matches Stripe's
 * `expired_card` decline ("Your card has expired."), which the backend relays
 * verbatim inside its "Payment declined — …" message. Treating that as a stale
 * quote silently re-previews and invites staff to re-confirm a dead card. Do not
 * widen this pattern.
 */
const QUOTE_EXPIRED_PATTERN = /quote has expired/i;

/**
 * Moves an existing subscriber up onto a dearer, active, annual plan.
 *
 * Only upward moves between active annual plans are offered: Stripe refuses an
 * inactive price on a subscription item, and downgrades are out of scope. The
 * amount is quoted by Stripe before confirming, and the quote's `proration_date`
 * is passed back on confirm so the charge matches the number staff were shown.
 */
const ChangePlan = ({ patient, subscription, onSuccess }) => {
  const dispatch = useAppDispatch();
  const plans = useAppSelector((state) => state.payments.clinic_payment_plans);
  const [plan, setPlan] = useState(null);
  const [preview, setPreview] = useState(null);
  // The plan the held quote was fetched for. Confirm is gated on this matching
  // the selected plan, so a preview can never be spent against another plan.
  const [previewPlanId, setPreviewPlanId] = useState(null);
  const [previewedAt, setPreviewedAt] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // Local, rather than the shared per-thunk redux flag: with two previews in
  // flight the redux flag clears when the FIRST one settles, which would enable
  // confirm while the request that owns the selected plan is still running.
  const [isPreviewing, setIsPreviewing] = useState(false);
  const isChanging = useIsLoading(api.Payments.changeSubscriptionPlan);
  // Monotonic id of the most recently issued preview request. Anything that
  // comes back with an older id lost the race and is discarded whole.
  const previewRequestRef = useRef(0);
  // Guards against a second confirm slipping through before `isChanging` (and
  // the dialog's own loading state) has disabled the button.
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (plans.length === 0) dispatch(api.Payments.getAllPaymentPlans());
  }, [dispatch, plans]);

  const currentPlan = subscription?.plan;
  // The backend refuses to compare prices when the current plan has no amount
  // (`cleanPlan` stores `amount: plan.amount || undefined`), so every plan we
  // could offer would 400. Offer none and say why.
  const hasComparableAmount = typeof currentPlan?.amount === 'number';
  const currentAmount = hasComparableAmount ? currentPlan.amount : 0;
  const currentPlanName = SubscriptionUtils.getSubscriptionName(subscription);

  // Active annual plans priced above the one the patient already holds. The
  // amount filter already excludes their current plan; the id check says so.
  const upgradePlans = useMemo(
    () =>
      hasComparableAmount
        ? plans.filter(
            ({ id, payment_plan }) =>
              payment_plan?.active &&
              payment_plan?.interval === 'year' &&
              (payment_plan?.amount || 0) > currentAmount &&
              id !== currentPlan?.id &&
              payment_plan?.id !== currentPlan?.id,
          )
        : [],
    [plans, currentAmount, currentPlan, hasComparableAmount],
  );

  // A quote goes stale on the wall clock, not on a render, so poll for it.
  useEffect(() => {
    if (!previewedAt) return undefined;

    const check = () => setIsStale(moment().unix() - previewedAt >= QUOTE_STALE_AFTER_SECONDS);
    check();
    const timer = setInterval(check, 15000);
    return () => clearInterval(timer);
  }, [previewedAt]);

  async function loadPreview(selectedPlan, successNotice = null) {
    // Claim the latest slot before dispatching. Any preview still in flight is
    // now stale and will be thrown away when it lands.
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;

    setPreview(null);
    setPreviewPlanId(null);
    setPreviewedAt(null);
    setIsStale(false);
    setError(null);
    setNotice(null);
    setIsPreviewing(true);

    const result = await dispatch(
      api.Payments.previewSubscriptionPlanChange({
        paymentPlanId: selectedPlan.id,
        patientId: patient.id,
      }),
      { meta: true },
    );

    // A slow request for a previously selected plan must not touch anything:
    // not the preview, not the error, not the timestamps, and not the loading
    // flag — clearing that would enable confirm while the newer request runs.
    if (previewRequestRef.current !== requestId) return;

    setIsPreviewing(false);

    if (!result.isSuccess) {
      setError(
        getApiErrorMessage(
          result.error,
          'Could not calculate the prorated amount. Try selecting the plan again.',
        ),
      );
      return;
    }

    setPreview(result.result);
    setPreviewPlanId(selectedPlan.id);
    setPreviewedAt(moment().unix());
    if (successNotice) setNotice(successNotice);
  }

  function onSelectPlan(selectedPlan) {
    setPlan(selectedPlan);
    loadPreview(selectedPlan);
  }

  async function onConfirm() {
    // The MUI dialog stays clickable through its exit transition, so a
    // double-click can reach here twice before `isChanging` flips.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      setError(null);
      setNotice(null);

      const result = await dispatch(
        api.Payments.changeSubscriptionPlan({
          paymentPlanId: plan.id,
          patientId: patient.id,
          planChangeRequest: { proration_date: preview.proration_date },
        }),
        // `meta` is kept so the decline message can be read off the error
        // payload; the success toast keeps this consistent with the sibling
        // actions in this file.
        {
          meta: true,
          successTitle: 'Change Plan',
          successText: 'Plan changed successfully!',
        },
      );

      if (result.isSuccess) {
        onSuccess();
        return;
      }

      const message = getApiErrorMessage(
        result.error,
        'The plan change did not go through. The patient remains on their current plan.',
      );

      // An expired quote is recoverable without losing the selection: fetch a
      // fresh one and ask staff to confirm the new number. See
      // QUOTE_EXPIRED_PATTERN — the match must not be widened.
      if (QUOTE_EXPIRED_PATTERN.test(message)) {
        await loadPreview(plan, `${message} A new quote has been loaded — review it and confirm.`);
        return;
      }

      setError(message);
    } finally {
      isSubmittingRef.current = false;
    }
  }

  const amountDue = preview?.amount_due;
  const credit = preview?.credit;
  const tax = preview?.tax;
  const newPeriodEnd = preview?.new_period_end;
  const hasQuote =
    !!preview && typeof preview.proration_date === 'number' && previewPlanId === plan?.id;
  // Never offer to confirm an amount we cannot name, or a quote that belongs to
  // a plan other than the selected one.
  const canConfirm =
    !!plan && hasQuote && typeof amountDue === 'number' && !isStale && !isPreviewing;

  return (
    <div className="add-subscription change-plan">
      <span className="current-plan">
        Currently on <strong>{currentPlanName}</strong>
        {currentAmount > 0 && ` — $${SubscriptionUtils.convertPlanAmount(currentAmount)}/year`}
      </span>

      <PlanChips
        title="Upgrade To"
        plans={upgradePlans}
        onClick={onSelectPlan}
        activeId={plan?.id}
      />

      {upgradePlans.length === 0 && (
        <span className="no-plans">
          {hasComparableAmount
            ? "There are no active annual plans priced above this patient's current plan."
            : "This patient's current plan has no price on record, so a plan change cannot be quoted here. It has to be handled manually."}
        </span>
      )}

      {!!plan && (
        <div className="plan-change-preview">
          {isPreviewing && <span className="preview-loading">Calculating the charge…</span>}
          {!isPreviewing && hasQuote && (
            <>
              <span className="preview-label">Charged today</span>
              <span className="preview-amount">
                {typeof amountDue === 'number'
                  ? `$${SubscriptionUtils.convertPlanAmount(amountDue)}`
                  : 'Unavailable'}
              </span>
              <ul className="preview-details">
                {typeof amountDue !== 'number' && (
                  <li>
                    Stripe did not return an amount for this quote. Refresh it before confirming.
                  </li>
                )}
                {!!credit && (
                  <li>
                    Includes a ${SubscriptionUtils.convertPlanAmount(credit)} credit for unused time
                    on {currentPlanName}.
                  </li>
                )}
                {!!tax && <li>Includes ${SubscriptionUtils.convertPlanAmount(tax)} tax.</li>}
                <li>
                  {newPeriodEnd
                    ? `Next renewal: ${moment.unix(newPeriodEnd).format('MMM D, YYYY')}.`
                    : 'Next renewal date unavailable — the change itself is unaffected.'}
                </li>
              </ul>
              {isStale && (
                <div className="preview-stale" role="alert">
                  <span>This quote has expired. Refresh it to see the current amount.</span>
                  <DefaultButton
                    name="Refresh Quote"
                    icon="arrows-rotate"
                    onClick={() => loadPreview(plan)}
                    loading={isPreviewing}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!!error && (
        <div className="plan-change-error" role="alert">
          {error}
        </div>
      )}

      {!!notice && (
        <div className="plan-change-notice" role="status" aria-live="polite">
          {notice}
        </div>
      )}

      <div className="button-wrapper">
        <ConfirmationButton
          name={plan ? 'Change to ' + getPlanDescription(plan) : 'Change Plan'}
          loading={isChanging}
          onClick={onConfirm}
          disabled={!canConfirm}
          className="blue"
          style={{ width: '100%' }}
          confirmation={{
            message: `This will immediately charge the patient $${SubscriptionUtils.convertPlanAmount(
              amountDue || 0,
            )} and move them from ${currentPlanName} to ${
              plan?.payment_plan?.name
            }. Are you sure you want to proceed?`,
            // Without this the dialog's own Confirm button stays enabled through
            // the MUI exit transition and can be clicked a second time.
            loadingState: isChanging,
          }}
        />
      </div>
    </div>
  );
};

/**
 * Pulls the API's own message out of a failed dispatch, so a declined card shows
 * staff Stripe's reason rather than a generic failure.
 */
function getApiErrorMessage(error, fallback) {
  // Only ever a message the server actually sent. `error.message` is the JS
  // Error's own text — for a network failure that is "Failed to fetch", which
  // says nothing useful about a charge; the friendly fallback is better.
  const body = error?.response?.jsonBody;
  let message = body?.message ?? error?.response?.jsonBodyMessage;
  if (message?.message) message = message.message;

  if (typeof message !== 'string' || !message.trim()) return fallback;

  return message.replace(/^Error:\s*/, '');
}

function getPlanDescription(plan = {}) {
  return `${plan.payment_plan?.name} - $${SubscriptionUtils.convertPlanAmount(
    plan.payment_plan?.amount || 0,
  )}/${plan.payment_plan?.interval}`;
}

const PlanChips = ({ plans = [], title, onClick, activeId, collapsed = false }) => {
  return (
    <UncontrolledCollapse title={title} initialValue={collapsed}>
      <div className="plans">
        {plans.map((plan) => (
          <DefaultChip
            key={plan.id}
            text={getPlanDescription(plan)}
            className={activeId === plan.id ? 'active' : ''}
            onClick={() => onClick(plan)}
          />
        ))}
      </div>
    </UncontrolledCollapse>
  );
};

const ChargePatientButton = (props) => {
  let [showModal, setShowModal] = useState(false);
  return (
    <div className="button-wrapper charge-patient">
      <DefaultButton
        icon="dollar-sign"
        name="Charge Patient"
        disabled={(props.creditCards || []).length === 0}
        onClick={() => setShowModal(true)}
      />
      {showModal && (
        <DefaultModal
          className="modal charge-patient-modal"
          wrap={true}
          padding={true}
          title="Charge Patient"
          content={
            <ChargePatientForm patient={props.patient} onSuccess={() => setShowModal(false)} />
          }
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

const ListItems = ({ creditCards = [], patient }) => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState();

  async function deleteCreditCard(card) {
    setLoading(true);
    await dispatch(api.Payments.deleteCreditCard({ patientId: patient.id, cardId: card.id }), {
      errorTitle: 'Credit Card',
    });
    setLoading(false);
  }

  function getBrandIcon(card) {
    let brand = card?.brand?.toLowerCase();
    if (brand === 'american express') brand = 'amex';
    return brand;
  }

  return (
    <>
      {!!confirmDelete && (
        <ConfirmationDialog
          title="Remove Credit Card"
          message="This will remove the credit card. Are you sure you want to proceed?"
          open={!!confirmDelete}
          onConfirm={async () => {
            await deleteCreditCard(confirmDelete);
            setConfirmDelete();
          }}
          onCancel={() => setConfirmDelete()}
          loadingState={loading}
        />
      )}
      {creditCards.map((card, index) => (
        <SelectableListItem
          disabled={loading}
          onClick={() => setConfirmDelete(card)}
          key={index}
          padded={false}
          item={
            <ListItem className="card-item">
              <ListItemAvatar>
                <Icon
                  fontAwesomeStyle="fab"
                  src={`cc-${getBrandIcon(card)}`}
                  width="40px"
                  alt={card.brand}
                />
              </ListItemAvatar>
              <ListItemText
                primary={<span>xxxx xxxx xxxx {card.last4}</span>}
                secondary={
                  <>
                    <span>
                      Exp: {card.exp_month}/{card.exp_year}
                    </span>
                    {isInvalidCard(card) && <InvalidCardBadge />}
                    {card.is_default && <DefaultCardBadge />}
                  </>
                }
              />
            </ListItem>
          }
        />
      ))}
    </>
  );
};

const DEFAULT_BAGE_STYLE = {
  textTransform: 'uppercase',
  float: 'right',
  fontSize: '10px',
};

const DefaultCardBadge = () => (
  <DefaultChip
    text="Default"
    backgroundColor="#63C073"
    textColor="white"
    style={DEFAULT_BAGE_STYLE}
  />
);

const InvalidCardBadge = () => (
  <DefaultChip
    label="Expired"
    backgroundColor="#ff3b5e"
    textColor="white"
    style={DEFAULT_BAGE_STYLE}
  />
);

function isInvalidCard(card) {
  let currentDate = moment();
  let currentYear = currentDate.tz(CalendarUtils.getTargetTimezone()).format('YY');
  let currentMonth = currentDate.tz(CalendarUtils.getTargetTimezone()).format('MM');

  return (
    Number(currentYear) > Number(card.exp_year) ||
    (Number(currentYear) === Number(card.exp_year) &&
      Number(currentMonth) <= Number(card.exp_month))
  );
}

CreditCardList.propTypes = {
  creditCards: PropTypes.array,
};

export default CreditCardList;
